import React, { useState, useEffect } from 'react';
import { Switch, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { CampaignAdditionalPermissionsDto, CampaignItemDto } from '@api/Api';
import {
  toggleCampaignInfoModal,
  toggleEntityDeletionModal
} from '@redux/actions/modalAction';
import { updateAdditionalPermissions } from '@redux/actions/campaignAction';
import AwDefaultSettings from '@components/Modals/CampaignInfoModal/AWDefaultSettings';
import { DeleteIcon } from '@icons';

function CampaignSettingsTab({ campaign }: { campaign: CampaignItemDto }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [additionalPermission, setAdditionalPermission] =
    useState<CampaignAdditionalPermissionsDto>(campaign.additionalPermissions);

  useEffect(() => {
    setAdditionalPermission(campaign.additionalPermissions);
  }, [campaign.additionalPermissions]);

  const onToggleAdditionalPermission = async (
    value: boolean,
    target: string
  ) => {
    const newAdditionPermission = {
      ...additionalPermission,
      [target]: value
    };
    setAdditionalPermission(newAdditionPermission);
    await updateAdditionalPermissions({
      campaignId: campaign.id,
      ...newAdditionPermission
    })(dispatch);
  };

  return (
    <>
      <div className="campaign_settigs_tab">
        <div className="campaign_settigs_tab__head">
          <div className="title">Contributor permissions</div>
          <div className="description">
            Set permissions for all contributors invited to this project
          </div>
        </div>
        <div className="action">
          <div className="action__title">Can download files</div>
          <div>
            <Switch
              className="campaign_settings_tab__switcher"
              disabled={!campaign.permissions.changeAdditionalPermissions}
              checked={additionalPermission.canDownloadAssets}
              onChange={(e) =>
                onToggleAdditionalPermission(e, 'canDownloadAssets')
              }
            />{' '}
            <span className="main-body-text main-body-text--semibold">
              {additionalPermission.canDownloadAssets ? 'On' : 'Off'}
            </span>
          </div>
        </div>
        <div className="action">
          <div className="action__title">Can invite other contributors</div>
          <div>
            <Switch
              className="campaign_settings_tab__switcher"
              disabled={!campaign.permissions.changeAdditionalPermissions}
              checked={additionalPermission.canInviteContributors}
              onChange={(e) =>
                onToggleAdditionalPermission(e, 'canInviteContributors')
              }
            />{' '}
            <span className="main-body-text main-body-text--semibold">
              {additionalPermission.canInviteContributors ? 'On' : 'Off'}
            </span>
          </div>
        </div>
        <div className="action">
          <div className="action__title">Can share external links</div>
          <div>
            <Switch
              className="campaign_settings_tab__switcher"
              disabled={!campaign.permissions.changeAdditionalPermissions}
              checked={additionalPermission.canShareExternalLinks}
              onChange={(e) =>
                onToggleAdditionalPermission(e, 'canShareExternalLinks')
              }
            />{' '}
            <span className="main-body-text main-body-text--semibold">
              {additionalPermission.canShareExternalLinks ? 'On' : 'Off'}
            </span>
          </div>
        </div>
        <div className="action">
          <div className="action__title">Can upload media to folders only</div>
          <div>
            <Switch
              className="campaign_settings_tab__switcher"
              disabled={!campaign.permissions.changeAdditionalPermissions}
              checked={!additionalPermission.canUploadToRoot}
              onChange={(e) =>
                onToggleAdditionalPermission(!e, 'canUploadToRoot')
              }
            />{' '}
            <span className="main-body-text main-body-text--semibold">
              {!additionalPermission.canUploadToRoot ? 'On' : 'Off'}
            </span>
          </div>
        </div>
      </div>
      <AwDefaultSettings
        campaignId={campaign.id}
        canChangeAWDefaultSettings={
          campaign.permissions.changeAWDefaultSettings
        }
      />
      <div className="campaign_settigs_tab">
        <div className="delete">
          <strong>Delete project</strong>
          <Button
            type="text"
            size="small"
            className="delete__btn"
            disabled={!campaign.permissions.delete}
            onClick={() => {
              dispatch(
                toggleEntityDeletionModal({
                  entity: campaign,
                  page: 'campaign',
                  permanent: false,
                  visible: true,
                  func: () => {
                    dispatch(toggleCampaignInfoModal(null));
                    navigate('../');
                  }
                })
              );
            }}
          >
            <DeleteIcon />
            <span>Delete</span>
          </Button>
        </div>
      </div>
    </>
  );
}

export default CampaignSettingsTab;
